import Icon from '$store/components/ui/Icon.tsx'
import type { FilterToggle, FilterToggleValue } from 'apps/commerce/types.ts'
import { FilterValues, MatcherFilters } from 'site/components/search/FilterValues.tsx'
import { FilterSetup } from 'site/components/search/SearchResult.tsx'

export type FilterToggleValueWithCategory = FilterToggleValue & {
    category: string
}

export interface FormattedFilters {
    filters?: FilterToggle[]
    selectedLabels?: FilterToggleValueWithCategory[]
    setup?: FilterSetup[]
}

/** @titleBy matcher */

export interface FilterProps {
    formattedFilters?: FormattedFilters
    isDepartment: boolean
    /**
     * @ignore
     */
    filtersModalCloseId?: string
    matchersFilters?: MatcherFilters[]
    currentPath?: string
    onClose?: () => void
}

function shouldProcessFilter(
    formattedFilters: FormattedFilters,
    filter: FilterToggle,
    currentPath?: string,
) {
    if (!formattedFilters || !currentPath) {
        const isFilterSelected = filter.values.some(({ selected }) => selected)
        return { isFilterOpen: isFilterSelected, isFilterRendered: isFilterSelected }
    }

    const filterConfig =
        formattedFilters.setup?.find((item) => item.label === filter.label)?.config || []
    const defaultConfig = filterConfig.find((configItem) =>
        configItem.url === '*' || configItem.url === '/*'
    )

    const currentConfig = filterConfig.find((configItem) => configItem.url === currentPath)

    const isFilterOpen = (currentConfig ? currentConfig.isOpen : defaultConfig?.isOpen) ||
        currentConfig?.keepOpen && filter.values.some(({ selected }) => selected)

    const isFilterRendered = currentConfig ? !currentConfig.notRender : !defaultConfig?.notRender

    return { isFilterOpen, isFilterRendered }
}

function Filters({
    formattedFilters,
    isDepartment,
    filtersModalCloseId,
    matchersFilters = [],
    currentPath,
    onClose,
}: FilterProps) {
    const filters = formattedFilters?.filters
    const selectedLabels = formattedFilters?.selectedLabels?.filter((item) => item.category !== 'available')
    if (!filters || !selectedLabels) {
        return null
    }

    return (
        <div class='p-2 top-4'>
            <ul class='pr-2 flex flex-col overflow-y-scroll scrollbar-none md:max-h-none '>
                <div class='flex flex-col border-b border-neutral-3 pb-4 relative'>
                    <div class='flex items-center gap-2 '>
                        <Icon id='Filter' width={16} height={16} class='text-primary-700' />
                        <span class='font-lato text-lg font-bold'>Filtrado por</span>
                    </div>

                    <button
                        type='button'
                        className='p-2 z-20 cursor-pointer xl:hidden absolute -top-1 right-0'
                        onClick={onClose}
                        aria-label='Fechar filtro'
                    >
                        <Icon id='XMark' class='text-neutral-0' width={24} height={24} />
                    </button>

                    {selectedLabels.length > 0 && (
                        <>
                            <div class='mt-4 flex items-center gap-2 flex-wrap'>
                                {selectedLabels.map((currentItem) => {
                                    return (
                                        <span class='py-[6px] font-lato text-sm px-[9px] text-center min-w-[70px] text-primary-700 border border-primary-700 rounded-full relative'>
                                            {currentItem.label}
                                            <span
                                                role='link'
                                                onClick={() => (location.href = currentItem.url)}
                                                class='cursor-pointer'
                                            >
                                                <Icon
                                                    id='Close'
                                                    width={15}
                                                    height={15}
                                                    class='absolute top-0 -right-1 cursor-pointer'
                                                />
                                            </span>
                                        </span>
                                    )
                                })}
                            </div>

                            <button
                                type='button'
                                onClick={() => {
                                    location.href = `${location.origin}/${
                                        location.pathname.split('/')[1]
                                    }`
                                }}
                                class='flex items-center gap-1 mt-4 justify-center cursor-pointer'
                            >
                                <span class='text-xs font-lato text-neutral-1 '>
                                    Limpar todos os Filtros X
                                </span>
                            </button>
                        </>
                    )}
                </div>
                {filters.map((filter) => {
                    const dic: { [key: string]: string } = {
                        Departments: 'Departamento',
                        Departamento: 'Departamento',
                        Brands: 'Marcas',
                        PriceRanges: 'Faixas de preço',
                    }

                    const { isFilterOpen, isFilterRendered } = shouldProcessFilter(
                        {
                            filters,
                            selectedLabels,
                            setup: formattedFilters?.setup,
                        },
                        filter,
                        currentPath,
                    )

                    if (!isFilterRendered) return null

                    return (
                        <li class='flex flex-col border-b border-neutral-3 last:border-none'>
                            <details class='group' open={isFilterOpen}>
                                <summary class='flex items-center justify-between cursor-pointer px-1.5'>
                                    <span class='block text-neutral-0 font-semibold font-lato py-4 '>
                                        {dic[filter.label] || filter.label}
                                    </span>
                                    <Icon
                                        id='Arrow'
                                        width={17}
                                        height={8}
                                        strokeWidth={16}
                                        class='rotate-0 group-open:rotate-180 transition-all ease-in-out duration-[400ms] text-primary-700'
                                    />
                                </summary>

                                {filter.label === 'Categoria' || filter.label === 'Departamento'
                                    ? (
                                        <FilterValues
                                            {...filter}
                                            matchersFilters={matchersFilters}
                                            placeholder={`Busque por ${filter.label.toLowerCase()}`}
                                        />
                                    )
                                    : filter.label === 'Tamanho'
                                    ? (
                                        <FilterValues
                                            {...filter}
                                            placeholder='Busque por tamanho'
                                            isDepartament={isDepartment}
                                        />
                                    )
                                    : <FilterValues {...filter} />}
                            </details>
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}

export default Filters
